import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import {
  DaDataSuggestOrganizationQuery,
  DaDataFindOrganizationByInnQuery,
} from "@/shared/graphql";
import type { DaDataOrganizationSuggestionType } from "@/shared/types/common";
import { DialogHeader, ResponsiveDialog } from "@/shared/components";

interface OrganizationSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (organization: DaDataOrganizationSuggestionType) => void;
  title?: string;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function isInn(value: string): boolean {
  const cleaned = value.replace(/\D/g, "");
  return cleaned.length === 10 || cleaned.length === 12;
}

export const OrganizationSearchDialog: React.FC<
  OrganizationSearchDialogProps
> = ({ open, onClose, onSelect, title = "Поиск организации" }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 200);

  const [
    suggestOrganization,
    { data: suggestData, loading: suggestLoading, error: suggestError },
  ] = useLazyQuery(DaDataSuggestOrganizationQuery, {
    fetchPolicy: "cache-and-network",
  });

  const [
    findByInn,
    { data: findData, loading: findLoading, error: findError },
  ] = useLazyQuery(DaDataFindOrganizationByInnQuery, {
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (debouncedSearchQuery.trim().length >= 3) {
      if (isInn(debouncedSearchQuery)) {
        const inn = debouncedSearchQuery.replace(/\D/g, "");
        findByInn({
          variables: {
            inn,
          },
        });
      } else {
        suggestOrganization({
          variables: {
            query: debouncedSearchQuery,
            count: 10,
          },
        });
      }
    }
  }, [debouncedSearchQuery, suggestOrganization, findByInn]);

  const suggestions = useMemo(() => {
    const data = findData || suggestData;
    return (
      data?.dadataFindOrganizationByInn?.suggestions ||
      data?.dadataSuggestOrganization?.suggestions ||
      []
    );
  }, [findData, suggestData]);

  const loading = suggestLoading || findLoading;
  const error = suggestError || findError;

  const handleSelectOrganization = useCallback(
    (suggestion: DaDataOrganizationSuggestionType) => {
      onSelect(suggestion);
      onClose();
      setSearchQuery("");
    },
    [onSelect, onClose]
  );

  const handleClose = useCallback(() => {
    onClose();
    setSearchQuery("");
  }, [onClose]);

  const getStatusChip = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <Chip label="Активна" color="success" size="small" />;
      case "LIQUIDATING":
        return <Chip label="Ликвидируется" color="warning" size="small" />;
      case "LIQUIDATED":
        return <Chip label="Ликвидирована" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={handleClose}>
      <DialogHeader title={title} onClose={handleClose} loading={loading} />

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{ p: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
        >
          <TextField
            fullWidth
            placeholder="Введите название организации или ИНН..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              },
            }}
            variant="outlined"
            size="medium"
            helperText="Поиск по названию, ИНН (10 или 12 цифр) или ОГРН"
          />
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">
                Ошибка при поиске организаций: {error.message}
              </Alert>
            </Box>
          )}

          {!loading &&
            !error &&
            searchQuery.trim().length > 0 &&
            searchQuery.trim().length < 3 && (
              <Box sx={{ p: 2 }}>
                <Alert severity="info">
                  Введите минимум 3 символа для поиска
                </Alert>
              </Box>
            )}

          {!loading &&
            !error &&
            debouncedSearchQuery.trim().length >= 3 &&
            suggestions.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Alert severity="warning">
                  По вашему запросу ничего не найдено
                </Alert>
              </Box>
            )}

          {suggestions.length > 0 && (
            <List sx={{ pt: 0 }}>
              {suggestions.map(
                (
                  suggestion: DaDataOrganizationSuggestionType,
                  index: number
                ) => (
                  <ListItemButton
                    key={index}
                    onClick={() => handleSelectOrganization(suggestion)}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      "&:last-child": {
                        borderBottom: 0,
                      },
                      py: 2,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="body1" fontWeight={500}>
                            {suggestion.value}
                          </Typography>
                          {getStatusChip(suggestion.data.state.status)}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            ИНН: {suggestion.data.inn} • КПП:{" "}
                            {suggestion.data.kpp} • ОГРН: {suggestion.data.ogrn}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {suggestion.data.address.value}
                          </Typography>
                          {suggestion.data.management && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {suggestion.data.management.post}:{" "}
                              {suggestion.data.management.name}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                )
              )}
            </List>
          )}

          {!loading && !error && searchQuery.trim().length === 0 && (
            <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              <BusinessIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                Поиск организации
              </Typography>
              <Typography variant="body2">
                Начните вводить название организации или ИНН
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </ResponsiveDialog>
  );
};
